/* Zenthesia research library: index labels, facts, evidence-at-a-glance rows, filters, sticky filter bar.
   init() re-reads the DOM, so it is safe to run again after the theme editor re-renders a section. */
(() => {
  const pad = (n) => String(n).padStart(2, '0');
  const esc = (t) => String(t).replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' })[c]);
  const num = (el, key) => Number(el.dataset[key] || 0);
  let state = null;
  let ticking = false;

  function updateStuck() {
    ticking = false;
    if (!state || !state.bar || !state.sentinel || !state.bar.isConnected) return;
    const { cats, bar, sentinel } = state;
    const visibleCats = cats.filter((c) => c.isConnected && !c.hidden);
    const last = visibleCats[visibleCats.length - 1];
    const stuck = Boolean(last) && sentinel.getBoundingClientRect().top < 0 && last.getBoundingClientRect().bottom > bar.offsetHeight;
    bar.classList.toggle('is-stuck', stuck);
    sentinel.style.height = stuck ? `${bar.offsetHeight}px` : '0px';
  }

  function init() {
    const cats = [...document.querySelectorAll('[data-zr-cat]')];
    const bar = document.querySelector('[data-zr-filters]');
    const sentinel = document.querySelector('[data-zr-filters-sentinel]');
    state = { cats, bar, sentinel };
    if (!cats.length) return;

    document.querySelectorAll('.zen-research a[href^="http"]').forEach((a) => {
      if (a.host && a.host !== location.host && !a.target) {
        a.target = '_blank';
        a.rel = 'noopener';
      }
    });

    cats.forEach((c, i) => {
      const label = c.querySelector('[data-zr-index]');
      if (label) label.textContent = `${pad(i + 1)} / ${pad(cats.length)}`;
    });

    const totals = cats.reduce(
      (t, c) => ({ count: t.count + num(c, 'zrCount'), rct: t.rct + num(c, 'zrRct'), animal: t.animal + num(c, 'zrAnimal') }),
      { count: 0, rct: 0, animal: 0 },
    );
    const facts = {
      total: [totals.count, totals.count === 1 ? 'study' : 'studies'],
      rct: [totals.rct, totals.rct === 1 ? 'randomized trial' : 'randomized trials'],
      animal: [totals.animal, totals.animal === 1 ? 'animal study, labeled' : 'animal studies, labeled'],
    };
    for (const [key, [n, label]] of Object.entries(facts)) {
      const el = document.querySelector(`[data-zr-fact="${key}"]`);
      if (!el) continue;
      el.innerHTML = `<b>${n}</b> ${label}`;
      el.hidden = false;
    }

    const overview = document.querySelector('[data-zr-overview]');
    const list = document.querySelector('[data-zr-glance]');
    if (overview && list) {
      const max = Math.max(1, ...cats.map((c) => num(c, 'zrCount')));
      list.innerHTML = cats
        .map((c) => {
          const n = num(c, 'zrCount');
          const r = num(c, 'zrRct');
          const a = num(c, 'zrAnimal');
          const label = `${c.dataset.zrName}: ${n} ${n === 1 ? 'study' : 'studies'}, ${r} randomized ${r === 1 ? 'trial' : 'trials'}, ${a} animal ${a === 1 ? 'study' : 'studies'}, latest ${c.dataset.zrLatest}. View studies`;
          return (
            `<li><a class="zr-g-row" href="#${esc(c.dataset.zrCat)}" aria-label="${esc(label)}">` +
            `<span class="zr-cond">${esc(c.dataset.zrName)}</span>` +
            `<span class="zr-g-count">${n}<span class="zr-bar" style="width:${Math.round((n / max) * 80)}px"></span></span>` +
            `<span class="zr-num zr-hide-sm">${r}</span>` +
            `<span class="zr-num zr-hide-sm">${a}</span>` +
            `<span class="zr-num">${esc(c.dataset.zrLatest)}</span>` +
            `<span class="zr-go"><b>View studies </b><i aria-hidden="true">→</i></span></a></li>`
          );
        })
        .join('');
      overview.hidden = false;
    }

    if (!bar || !sentinel) return;
    const studies = [...document.querySelectorAll('.zr-study')];
    const boxes = { rct: bar.querySelector('#zr-f-rct'), human: bar.querySelector('#zr-f-human'), body: bar.querySelector('#zr-f-body') };
    if (!boxes.rct || !boxes.human || !boxes.body) return;
    const countLabel = bar.querySelector('[data-zr-count-label]');
    const empty = bar.querySelector('[data-zr-empty]');
    const clear = bar.querySelector('[data-zr-clear]');

    function apply() {
      let shown = 0;
      for (const a of studies) {
        const ok =
          (!boxes.rct.checked || a.dataset.rct === 'true') &&
          (!boxes.human.checked || a.dataset.animal === 'false') &&
          (!boxes.body.checked || a.dataset.body === 'true');
        a.hidden = !ok;
        if (ok) shown++;
      }
      for (const c of cats) {
        const total = c.querySelectorAll('.zr-study').length;
        const visible = c.querySelectorAll('.zr-study:not([hidden])').length;
        c.hidden = visible === 0;
        const note = c.querySelector('[data-zr-showing]');
        if (note) note.textContent = visible === total ? '' : ` · showing ${visible}`;
      }
      if (countLabel) countLabel.textContent = `Showing ${shown} of ${studies.length}`;
      const wasEmpty = empty ? !empty.hidden : false;
      if (empty) empty.hidden = shown !== 0;
      if (shown === 0 && !wasEmpty) sentinel.scrollIntoView({ block: 'start' });
      updateStuck();
    }

    Object.values(boxes).forEach((b) => { b.onchange = apply; });
    if (clear) {
      clear.onclick = () => {
        Object.values(boxes).forEach((b) => { b.checked = false; });
        apply();
        boxes.rct.focus();
      };
    }
    bar.hidden = false;
    apply();
  }

  window.addEventListener('scroll', () => {
    if (!ticking) {
      ticking = true;
      requestAnimationFrame(updateStuck);
    }
  }, { passive: true });
  window.addEventListener('resize', updateStuck);
  document.addEventListener('shopify:section:load', init);
  document.addEventListener('shopify:section:reorder', init);
  document.addEventListener('shopify:section:unload', () => setTimeout(init, 0));
  init();
})();
